import { Link, useNavigate, useParams } from "@tanstack/react-router";

import {
  useArchivePlantProfile,
  usePlantProfile,
} from "@/lib/api/plant-profiles";
import { usePreferences } from "@/lib/preferences";
import { ecUnitLabel, formatEc, formatTemp, tempUnitLabel } from "@/lib/units";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorState } from "@/components/error-state";
import { AuditLogViewer } from "@/components/audit-log-viewer";

export function PlantProfileDetailPage() {
  const { plantProfileId } = useParams({
    from: "/chemistry-profile/plant-profiles/$plantProfileId",
  });
  const navigate = useNavigate();
  const { temperatureUnit, ecUnit } = usePreferences();
  const q = usePlantProfile(plantProfileId);
  const archive = useArchivePlantProfile(plantProfileId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  const p = q.data;
  const editable = !p.system_provided && !p.archived_at;

  function onArchive() {
    const reason =
      window.prompt("Reason for archiving this plant profile?") ||
      "user archive";
    archive.mutate(reason, {
      onSuccess: () => navigate({ to: "/chemistry-profile/plant-profiles" }),
    });
  }

  const subtitle = [p.species, p.category, p.stage, p.feeder_class]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="space-y-6">
      <Link
        to="/chemistry-profile/plant-profiles"
        className="text-sm text-muted-foreground hover:text-foreground"
      >
        ← Plant profiles
      </Link>

      <header className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{p.name}</h1>
          <p className="text-sm text-muted-foreground">
            {subtitle || "—"}
            {p.system_provided && " · system-provided"}
            {p.archived_at && " · archived"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {p.system_provided && (
            <span className="rounded-md border px-2.5 py-1 text-xs text-muted-foreground">
              Read-only · system-provided
            </span>
          )}
          {editable && (
            <>
              <Link
                to="/chemistry-profile/plant-profiles/$plantProfileId/edit"
                params={{ plantProfileId: p.id }}
              >
                <Button variant="outline">Edit</Button>
              </Link>
              <Button
                variant="ghost"
                onClick={onArchive}
                disabled={archive.isPending}
              >
                {archive.isPending ? "Archiving…" : "Archive"}
              </Button>
            </>
          )}
          <Link
            to="/chemistry-profile/plant-profiles/new"
            search={{ from: p.id }}
          >
            <Button variant="outline">Clone</Button>
          </Link>
        </div>
      </header>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Target band <span className="normal-case">(min · ideal · max)</span>
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <BandCard
            title="pH"
            min={p.ph_min}
            ideal={p.ph_ideal}
            max={p.ph_max}
          />
          <BandCard
            title={ecUnitLabel(ecUnit)}
            min={p.ec_min}
            ideal={p.ec_ideal}
            max={p.ec_max}
            format={(n) => formatEc(n, ecUnit)}
          />
          <BandCard
            title={`Temp (${tempUnitLabel(temperatureUnit)})`}
            min={p.temp_min_c}
            ideal={p.temp_ideal_c}
            max={p.temp_max_c}
            format={(n) => formatTemp(n, temperatureUnit)}
          />
        </div>
      </section>

      {p.notes && (
        <section className="space-y-1">
          <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Notes
          </h2>
          <p className="whitespace-pre-wrap text-sm">{p.notes}</p>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Audit trail
        </h2>
        <AuditLogViewer entityType="plant_profile" entityID={p.id} />
      </section>
    </div>
  );
}

function BandCard({
  title,
  min,
  ideal,
  max,
  format = (n) => (n === undefined ? "—" : String(n)),
}: {
  title: string;
  min?: number;
  ideal?: number;
  max?: number;
  format?: (n: number | undefined) => string;
}) {
  const empty = min === undefined && ideal === undefined && max === undefined;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {empty ? (
          <p className="text-sm text-muted-foreground">not set</p>
        ) : (
          <p className="text-base tabular-nums">
            <span className="text-muted-foreground">{format(min)}</span>
            {" · "}
            <span className="font-semibold">{format(ideal)}</span>
            {" · "}
            <span className="text-muted-foreground">{format(max)}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
